/** Monochrome line-art equipment icons for the Plant Mimic — matches the
 * reference PFD visual language (reactor/separator/column/tank/scrubber/granulator).
 * Colour is intentionally neutral (icons are structural, not status) per design system §2.2.
 */
const STROKE = "var(--oim-text)";
const W = 1.8;
const W_THIN = 1.1;

export function TankIcon({ size = 34 }) {
  return (
    <svg width={size} height={size * 1.5} viewBox="0 0 34 51" fill="none" stroke={STROKE} strokeWidth={W}>
      <path d="M6 10 Q17 2 28 10" />
      <rect x="6" y="10" width="22" height="34" rx="1" />
      <line x1="6" y1="44" x2="28" y2="44" />
      <line x1="0" y1="44" x2="34" y2="44" strokeWidth={W_THIN} />
    </svg>
  );
}

export function ReactorIcon({ size = 34 }) {
  return (
    <svg width={size} height={size * 1.5} viewBox="0 0 34 51" fill="none" stroke={STROKE} strokeWidth={W}>
      <path d="M6 12 Q17 4 28 12" />
      <rect x="6" y="12" width="22" height="30" rx="1" />
      <line x1="17" y1="2" x2="17" y2="14" />
      <line x1="17" y1="20" x2="17" y2="38" />
      <line x1="10" y1="24" x2="24" y2="24" strokeWidth={W_THIN} />
      <line x1="10" y1="33" x2="24" y2="33" strokeWidth={W_THIN} />
    </svg>
  );
}

export function ReactorCatalystIcon({ size = 34 }) {
  return (
    <svg width={size} height={size * 1.5} viewBox="0 0 34 51" fill="none" stroke={STROKE} strokeWidth={W}>
      <path d="M6 10 Q17 2 28 10" />
      <rect x="6" y="10" width="22" height="34" rx="1" />
      <line x1="9" y1="19" x2="25" y2="19" strokeWidth={W_THIN} strokeDasharray="2.5 2" />
      <line x1="9" y1="26" x2="25" y2="26" strokeWidth={W_THIN} strokeDasharray="2.5 2" />
      <line x1="9" y1="33" x2="25" y2="33" strokeWidth={W_THIN} strokeDasharray="2.5 2" />
    </svg>
  );
}

export function SeparatorIcon({ size = 34 }) {
  return (
    <svg width={size} height={size * 1.5} viewBox="0 0 34 51" fill="none" stroke={STROKE} strokeWidth={W}>
      <rect x="2" y="19" width="30" height="15" rx="5" />
      <line x1="2" y1="26.5" x2="32" y2="26.5" strokeWidth={W_THIN} />
      <line x1="10" y1="34" x2="8" y2="40" strokeWidth={W_THIN} />
      <line x1="24" y1="34" x2="26" y2="40" strokeWidth={W_THIN} />
    </svg>
  );
}

export function ColumnIcon({ size = 34 }) {
  return (
    <svg width={size} height={size * 1.5} viewBox="0 0 34 51" fill="none" stroke={STROKE} strokeWidth={W}>
      <path d="M13 5 Q17 2 21 5" />
      <rect x="13" y="5" width="8" height="42" rx="1" />
      {[12, 18.5, 25, 31.5, 38].map((y) => (
        <line key={y} x1="13" y1={y} x2="21" y2={y} strokeWidth={W_THIN} />
      ))}
    </svg>
  );
}

export function ScrubberIcon({ size = 34 }) {
  return (
    <svg width={size} height={size * 1.5} viewBox="0 0 34 51" fill="none" stroke={STROKE} strokeWidth={W}>
      <line x1="17" y1="2" x2="17" y2="8" />
      <path d="M11 8 L23 8 L17 14 Z" />
      <rect x="7" y="15" width="20" height="29" rx="1" />
      <line x1="10" y1="20" x2="15" y2="25" strokeWidth={W_THIN} />
      <line x1="15" y1="20" x2="10" y2="25" strokeWidth={W_THIN} />
      <line x1="19" y1="27" x2="24" y2="32" strokeWidth={W_THIN} />
      <line x1="24" y1="27" x2="19" y2="32" strokeWidth={W_THIN} />
      <line x1="10" y1="34" x2="15" y2="39" strokeWidth={W_THIN} />
      <line x1="15" y1="34" x2="10" y2="39" strokeWidth={W_THIN} />
    </svg>
  );
}

export function GranulatorIcon({ size = 34 }) {
  return (
    <svg width={size} height={size * 1.5} viewBox="0 0 34 51" fill="none" stroke={STROKE} strokeWidth={W}>
      <ellipse cx="17" cy="24" rx="15" ry="11" />
      <path d="M5 19 L29 29" strokeWidth={W_THIN} strokeDasharray="2.5 2" />
      <path d="M5 29 L29 19" strokeWidth={W_THIN} strokeDasharray="2.5 2" />
      <line x1="17" y1="35" x2="17" y2="42" />
    </svg>
  );
}

export function GranulesIcon({ size = 34 }) {
  const dots = [[9, 33], [16, 29], [23, 34], [12, 39], [21, 40], [27, 32]];
  return (
    <svg width={size} height={size * 1.5} viewBox="0 0 34 51" fill="none" stroke={STROKE} strokeWidth={W}>
      <path d="M7 25 L27 25 L23 10 L11 10 Z" />
      {dots.map(([cx, cy], i) => <circle key={i} cx={cx} cy={cy} r="2.4" fill={STROKE} stroke="none" />)}
    </svg>
  );
}

export const EQUIPMENT_ICONS = {
  tank: TankIcon,
  reactor: ReactorIcon,
  reactor_catalyst: ReactorCatalystIcon,
  separator: SeparatorIcon,
  column: ColumnIcon,
  scrubber: ScrubberIcon,
  granulator: GranulatorIcon,
  granules: GranulesIcon,
};

export function EquipmentIconRow({ icons, size = 30 }) {
  if (!icons?.length) return null;
  return (
    <div className="flex items-end gap-2" style={{ color: "var(--oim-text)" }} data-testid="mimic-equipment-icons">
      {icons.map((kind, i) => {
        const Icon = EQUIPMENT_ICONS[kind];
        return Icon ? <Icon key={i} size={size} /> : null;
      })}
    </div>
  );
}
